/**
 * MyReceipts — receipt history + contribution points.
 *
 * Route: /food-pulse/receipts/mine
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, Award, Camera, Loader2, ArrowRight, Check, Clock, X } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";
const RED   = "#7B2A2E";

const VERTICAL_ICON = {
  grocery: "🛒", restaurant: "🍽️", cafe: "☕", farmers: "🥬",
  retail: "🛍️", pharmacy: "💊", fuel: "⛽", other: "🧾",
};

const STATUS_LABEL = {
  uploaded:        { label: "Uploaded",        color: "#6B7280", icon: Clock },
  parsing:         { label: "Parsing",         color: "#6B7280", icon: Loader2 },
  parsed:          { label: "AI parsed",       color: "#F59E0B", icon: Check },
  "awaiting-parse":{ label: "Manual entry",    color: "#F59E0B", icon: Clock },
  "parse-failed":  { label: "Parse failed",    color: "#DC2626", icon: X },
  confirmed:       { label: "In review",       color: "#3B82F6", icon: Clock },
  approved:        { label: "Approved",        color: "#059669", icon: Check },
  rejected:        { label: "Rejected",        color: "#DC2626", icon: X },
};

export default function MyReceipts() {
  const { user } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [contrib,  setContrib]  = useState({ total_points: 0, events: [] });
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    Promise.all([
      http.get("/receipts/mine"),
      http.get("/user/contributions"),
    ]).then(([r, c]) => {
      setReceipts(r.data.items || []);
      setContrib(c.data || { total_points: 0, events: [] });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
        <div className="max-w-md text-center">
          <Receipt className="w-14 h-14 mx-auto mb-4" style={{ color: AMBER }} />
          <h1 className="font-display text-3xl mb-2">Sign in to see your receipts</h1>
          <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }}>
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="my-receipts">
      <SeoHead section="food-pulse-my-receipts" title="My receipts — Food Pulse"
               description="Your scanned receipts and contribution points." />

      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>— Contributions</div>
            <h1 className="font-display text-4xl">Your receipts &amp; points</h1>
          </div>
          <Link to="/food-pulse/receipts/new"
                className="rounded-full px-4 py-2 text-sm font-semibold text-white flex items-center gap-2 shadow"
                style={{ background: GREEN }}
                data-testid="receipts-new">
            <Camera className="w-4 h-4" /> Snap a receipt
          </Link>
        </div>

        {/* Points card ---------------------------------------- */}
        <div className="rounded-2xl bg-white border p-5 mb-6 flex items-center gap-4"
             style={{ borderColor: AMBER, background: "linear-gradient(90deg,#FFEDD5,#FFF7E4)" }}
             data-testid="receipts-points-card">
          <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
               style={{ background: "#FDE68A" }}>
            <Award className="w-7 h-7" style={{ color: RED }} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Contribution points</div>
            <div className="font-display text-4xl font-bold" style={{ color: GREEN }}>{contrib.total_points}</div>
            <div className="text-xs opacity-70">
              from {contrib.count} events · +2 per upload · +5 per confirm · +1 per matched line
            </div>
          </div>
        </div>

        {/* Receipts list -------------------------------------- */}
        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin inline opacity-50" /></div>
        ) : receipts.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed p-14 text-center"
               style={{ borderColor: AMBER, background: "#FFF7E4" }}
               data-testid="receipts-empty">
            <Receipt className="w-14 h-14 mx-auto opacity-40 mb-3" />
            <div className="font-display text-2xl mb-2">No receipts yet</div>
            <p className="opacity-70 text-sm max-w-md mx-auto mb-4">
              Snap your first grocery receipt to start earning contribution points and improve FoodPulse's price data.
            </p>
            <Link to="/food-pulse/receipts/new"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white"
                  style={{ background: GREEN }}>
              <Camera className="w-4 h-4" /> Snap first receipt
            </Link>
          </div>
        ) : (
          <div className="grid gap-3">
            {receipts.map((r) => {
              const s = STATUS_LABEL[r.status] || STATUS_LABEL.uploaded;
              const Ico = s.icon;
              const points = (r.matched_count || 0) + 5;
              return (
                <div key={r.id} className="rounded-2xl bg-white border p-3 flex items-center gap-3"
                     style={{ borderColor: "#E9D8B4" }}
                     data-testid={`receipt-row-${r.id}`}>
                  <div className="w-16 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-amber-50">
                    {r.image_url && (
                      <img src={r.image_url.startsWith("http") ? r.image_url : `${process.env.REACT_APP_BACKEND_URL}${r.image_url}`}
                           alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                      <span title={r.vertical}>{VERTICAL_ICON[r.vertical] || "🧾"}</span>
                      {r.store_name || "Unknown store"}
                    </div>
                    <div className="text-xs opacity-70 flex items-center gap-2 flex-wrap">
                      {r.receipt_date && <span>{r.receipt_date.slice(0, 10)}</span>}
                      {r.total != null && <span>${r.total}</span>}
                      {r.matched_count != null && r.matched_count > 0 && (
                        <span className="rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                              style={{ background: "#DCFCE7", color: GREEN }}>
                          {r.matched_count} matched
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[11px]" style={{ color: s.color }}>
                      <Ico className={`w-3 h-3 ${r.status === "parsing" ? "animate-spin" : ""}`} />
                      {s.label}
                    </div>
                  </div>
                  {r.status === "approved" && (
                    <div className="text-[11px] rounded-full px-2 py-0.5 font-bold flex-shrink-0"
                         style={{ background: "#FFEDD5", color: RED }}>
                      +{points} pts
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
